import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Paper from 'components/paper';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import titleCase from 'utils/title-case';
import { runScenario } from 'views/playground/features/bluviu-nutrition-r2/api/run-scenario';
import { useScenarioPlanState } from 'views/playground/features/bluviu-nutrition-r2/context/scenario-plan-context';
import { RunScenarioInput } from 'views/playground/features/bluviu-nutrition-r2/types/run-scenario-input.type';
import { StepProps } from 'views/playground/features/bluviu-nutrition-r2/types/step-props.type';

const StepFour: React.FC<StepProps> = ({ onClick }) => {
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();

  const {
    scenarioPlanQuery: { data },
  } = useScenarioPlanState();

  if (!data) return;

  const StepOne = () => {
    if (!data.stepOneFields) return;

    return (
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography variant="h3">Overview</Typography>
        <Divider flexItem dark variant="middle" />
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          {Object.entries(data.stepOneFields).map(([key, value]) => (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              borderBottom={`1px dotted ${theme.palette.darkBlue.main}`}>
              <Typography>{titleCase(key)}</Typography>
              <Typography fontWeight={'700'}>{titleCase(value)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const StepTwo = () => {
    if (!data.stepTwoFields) return;
    const { feedProgram } = data.stepTwoFields;
    return (
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography variant="h3">Feed Programs</Typography>
        <Divider flexItem dark variant="middle" />
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <List
            dark
            options={[
              { label: 'Feed', id: 'feed' },
              { label: 'Fish Size', id: 'fishSize' },
              { label: 'Price', id: 'price' },
            ]}>
            {feedProgram.map((line) => (
              <ListRow>
                <ListCell>{line.feed}</ListCell>
                <ListCell>{line.fishSize}</ListCell>
                <ListCell>{line.price}</ListCell>
              </ListRow>
            ))}
          </List>
        </Box>
      </Box>
    );
  };

  const StepThree = () => {
    if (!data.stepThreeFields) return;

    return (
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography variant="h3">Finance</Typography>
        <Divider flexItem dark variant="middle" />
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          {Object.entries(data.stepThreeFields).map(([key, value]) => (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
              borderBottom={`1px dotted ${theme.palette.darkBlue.main}`}>
              <Typography>{titleCase(key)}</Typography>
              <Typography fontWeight={'700'}>{titleCase(value)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const defaultValues = useMemo(() => {
    return {
      id: data.id,
    };
  }, [data]);

  const { handleSubmit } = useForm<RunScenarioInput>({
    defaultValues: defaultValues, // step default form values
  });

  const beginScenario = usePost<string, RunScenarioInput>(
    (request) => runScenario(request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Success!' },
      });
    },
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Fail!' },
      });
    }
  );

  const handleUpdate = (data: RunScenarioInput) => {
    beginScenario.mutate(data);
  };

  return (
    <Paper
      sx={{
        height: '100%',
      }}>
      <form
        onSubmit={handleSubmit(handleUpdate)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 32,
        }}>
        <Box flexGrow={1} display={'flex'} flexDirection={'column'} gap={8}>
          <StepOne />
          <StepTwo />
          <StepThree />
        </Box>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={4}>
          <Button disabled={beginScenario.isPending} onClick={() => onClick(2)}>
            Back
          </Button>
          <Button
            disabled={beginScenario.isPending}
            startIcon={beginScenario.isPending ? <CircularProgress color="inherit" size={18} /> : null}
            type="submit">
            Run
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default StepFour;
